import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  ClipboardCheck,
  Calendar,
  MapPin,
  Truck,
  ArrowDownCircle,
  ArrowUpCircle,
  Home,
} from 'lucide-react';

/**
 * Mobile Inspections list — shows every draft pre/post hire inspection a
 * yard dispatcher can action. Tapping a row opens the detail form where
 * they fill the checklist, snap photos, and collect a customer signature.
 */
const MobileInspections = () => {
  const navigate = useNavigate();
  const { api, operator } = useMobileAuth();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInspections = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/mobile/hire-inspections/pending');
      setInspections(res.data || []);
    } catch (err) {
      const status = err.response?.status;
      // Surface the backend's actual reason — three different things look like
      // a 403 here ("yard role required", "mobile session required", "operator
      // not found"), and a generic toast made debugging painful.
      const detail = err.response?.data?.detail;
      if (status === 403 || status === 401) {
        toast.error(detail || 'You are not authorised as a yard dispatcher.');
        navigate('/mobile/dashboard');
      } else {
        toast.error(detail || 'Failed to load inspections');
      }
    } finally {
      setLoading(false);
    }
  }, [api, navigate]);

  useEffect(() => {
    if (operator == null) return;  // wait for context hydration
    if (!operator.is_yard_dispatcher) {
      toast.error('Yard dispatcher role required.');
      navigate('/mobile/dashboard');
      return;
    }
    fetchInspections();
  }, [operator, fetchInspections, navigate]);

  const preInspections = inspections.filter(i => i.inspection_type === 'pre_hire');
  const postInspections = inspections.filter(i => i.inspection_type === 'post_hire');

  return (
    <div className="min-h-screen bg-[var(--m-bg)]" data-testid="mobile-inspections-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[var(--m-bg-card)] border-b border-[var(--m-border)] p-4 safe-top">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/mobile/dashboard')}
            className="text-[var(--m-text)] -ml-2"
            data-testid="inspections-back-btn"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-[var(--m-text)] font-semibold text-lg flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-[#ffd108]" />
              Dry-Hire Inspections
            </h1>
            <p className="text-[var(--m-text-secondary)] text-xs">Pre &amp; post hire checks · {inspections.length} pending</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-6 pb-40">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : inspections.length === 0 ? (
          <div className="text-center py-20">
            <ClipboardCheck className="h-12 w-12 text-gray-600 mx-auto mb-3" />
            <p className="text-[var(--m-text-secondary)]">No pending inspections</p>
            <p className="text-gray-600 text-xs mt-1">New dry-hire jobs will appear here</p>
          </div>
        ) : (
          <>
            <InspectionSection
              title="Pre-Hire (Dispatch)"
              icon={<ArrowUpCircle className="h-4 w-4 text-emerald-400" />}
              items={preInspections}
              onOpen={(id) => navigate(`/mobile/inspections/${id}`)}
            />
            <InspectionSection
              title="Post-Hire (Return)"
              icon={<ArrowDownCircle className="h-4 w-4 text-sky-400" />}
              items={postInspections}
              onOpen={(id) => navigate(`/mobile/inspections/${id}`)}
            />
          </>
        )}
      </div>
    </div>
  );
};

const InspectionSection = ({ title, icon, items, onOpen }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h2 className="text-[var(--m-text-body)] text-sm font-medium mb-2 flex items-center gap-2">
        {icon}
        {title} <span className="text-[var(--m-text-muted)]">· {items.length}</span>
      </h2>
      <div className="space-y-2">
        {items.map(insp => (
          <InspectionRow key={insp.id} inspection={insp} onOpen={() => onOpen(insp.id)} />
        ))}
      </div>
    </div>
  );
};

const InspectionRow = ({ inspection, onOpen }) => {
  const {
    job_number, customer_name, site_name, asset_equipment_type,
    job_start_date, job_end_date, pickup_from_yard, return_to_yard,
    inspection_type,
  } = inspection;
  const dateLabel = inspection_type === 'pre_hire'
    ? (job_start_date ? `Dispatch ${job_start_date}` : 'Dispatch pending')
    : (job_end_date ? `Return ${job_end_date}` : 'Return pending');
  const yardFlag = inspection_type === 'pre_hire' ? pickup_from_yard : return_to_yard;
  return (
    <Card
      onClick={onOpen}
      className="bg-[var(--m-bg-card)] border-[var(--m-border)] hover:border-[#ffd108] cursor-pointer transition-colors"
      data-testid={`inspection-row-${inspection.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[#ffd108] font-semibold text-sm">
                #{job_number || '—'}
              </span>
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/40 text-[10px] px-1.5 py-0">DRY</Badge>
              {yardFlag && (
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/40 text-[10px] px-1.5 py-0 gap-0.5">
                  <Home className="h-2.5 w-2.5" />
                  YARD
                </Badge>
              )}
            </div>
            <p className="text-[var(--m-text)] text-sm font-medium truncate">{customer_name}</p>
            <div className="flex items-center gap-3 text-xs text-[var(--m-text-secondary)] mt-1 flex-wrap">
              <span className="flex items-center gap-1 truncate max-w-[150px]">
                <Truck className="h-3 w-3" />{asset_equipment_type}
              </span>
              {site_name && (
                <span className="flex items-center gap-1 truncate max-w-[150px]">
                  <MapPin className="h-3 w-3" />{site_name}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />{dateLabel}
              </span>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[var(--m-text-muted)] shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

export default MobileInspections;
